import React, { FunctionComponent } from 'react';

import { useIntl } from 'react-intl';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { FormState, FormAction } from './reducer';
import { useMediaQueryContext } from '../../../../providers/MediaQueryProvider';

/**
 * Main content of step aka text
 */
type WizardSteps = {
  step: number;
};

const WizardSteps: FunctionComponent<WizardSteps> = ({ step }) => {
  const { formatMessage } = useIntl();

  return (
    <Box py={2}>
      <Typography>
        {formatMessage({ id: `features.withLoginWizard.step${step}` })}
      </Typography>
    </Box>
  );
};

/**
 * Secondary content of step aka input or image
 */
type WizardSecondaryStep = {
  formDispatch: React.Dispatch<FormAction>;
  formState: FormState;
  step: number;
};

type Option = {
  id: string;
  key: string;
  label: string;
};

export const WizardSecondaryStep: FunctionComponent<WizardSecondaryStep> = ({
  formDispatch,
  formState: { country, city, club, bar },
  step,
}) => {
  const onChangeText = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    type: 'CITY' | 'BAR' | 'CLUB'
  ) => {
    formDispatch({ type, payload: event.target.value });
  };

  const onChangeAutocomplete = (value: Option, type: 'COUNTRY') => {
    formDispatch({ type, payload: value });
  };

  switch (step) {
    case 1:
      return (
        <CountryCitySelect
          country={country}
          city={city}
          onChangeCountry={(event, value) => {
            onChangeAutocomplete(value, 'COUNTRY');
          }}
          onChangeCity={(event) => onChangeText(event, 'CITY')}
        />
      );
    case 2:
      return (
        <ClubBarSelect
          city={city}
          bar={bar}
          club={club}
          onChangeBar={(event) => onChangeText(event, 'BAR')}
          onChangeClub={(event) => onChangeText(event, 'CLUB')}
        />
      );
    case 3:
    case 4:
    case 5:
    case 6:
      return <MockUps step={step} />;
    default:
      return null;
  }
};

export default WizardSteps;

/**
 *
 */
type CountryCitySelect = {
  country: { id: string; key: string; label: string };
  city: string;
  onChangeCountry: (event: React.ChangeEvent<{}>, value: Option) => void;
  onChangeCity: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => any;
};

const CountryCitySelect: FunctionComponent<CountryCitySelect> = ({
  country,
  city,
  onChangeCountry,
  onChangeCity,
}) => {
  const { formatMessage } = useIntl();

  const countries: Option[] = [
    { id: 'aut', key: 'Austria' },
    { id: 'bel', key: 'Belgium' },
    { id: 'ch', key: 'Switzerland' },
    { id: 'de', key: 'Germany' },
    { id: 'dk', key: 'Denmark' },
    { id: 'fr', key: 'France' },
    { id: 'es', key: 'Spain' },
    { id: 'irl', key: 'Ireland' },
    { id: 'it', key: 'Italy' },
    { id: 'lux', key: 'Luxembourg' },
    { id: 'nl', key: 'Netherlands' },
    { id: 'nor', key: 'Norway' },
    { id: 'pol', key: 'Poland' },
    { id: 'pt', key: 'Portugal' },
    { id: 'swe', key: 'Sweden' },
    { id: 'uk', key: 'United Kingdom' },
  ].map(({ id, key }) => {
    return { id, key, label: formatMessage({ id: `general.countries.${id}` }) };
  });

  return (
    <Box py={2} textAlign="center">
      <Autocomplete
        disableClearable
        getOptionSelected={(option) => {
          return option.id === country.id;
        }}
        defaultValue={countries.find((country) => country.id === 'de')}
        id="country"
        options={countries}
        getOptionLabel={(option) => {
          return option.label;
        }}
        tabIndex={-1}
        value={country}
        onChange={onChangeCountry}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label={formatMessage({ id: 'general.country' })}
          />
        )}
      />
      <TextField
        autoFocus
        fullWidth
        id="city"
        label={formatMessage({ id: `features.withLoginWizard.city` })}
        margin="normal"
        onChange={onChangeCity}
        tabIndex={0}
        value={city}
      />
    </Box>
  );
};

/**
 *
 */
type ClubBarSelect = {
  bar: string;
  club: string;
  city: string;
  onChangeClub: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => any;
  onChangeBar: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => any;
};

const ClubBarSelect: FunctionComponent<ClubBarSelect> = ({
  bar,
  club,
  city,
  onChangeClub,
  onChangeBar,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box py={2} textAlign="center">
      <Box>
        <TextField
          autoFocus
          fullWidth
          id="club"
          label={formatMessage(
            { id: `features.withLoginWizard.club` },
            { city }
          )}
          margin="normal"
          onChange={onChangeClub}
          tabIndex={0}
          value={club}
        />
      </Box>
      <Box>
        <TextField
          fullWidth
          id="bar"
          label={formatMessage(
            { id: `features.withLoginWizard.bar` },
            { city }
          )}
          margin="normal"
          onChange={onChangeBar}
          tabIndex={0}
          value={bar}
        />
      </Box>
    </Box>
  );
};

/**
 *
 */
type MockUps = {
  step: 3 | 4 | 5 | 6;
};

const MockUps: FunctionComponent<MockUps> = ({ step }) => {
  const { width } = useMediaQueryContext();

  const image = ['list', 'map', 'meet', 'chat'][step - 3];

  return (
    <Box textAlign="center" mt={width < 960 ? 5 : 0}>
      <img alt="Mock" src={`./assets/${image}.png`} height="480" />
    </Box>
  );
};
